import React from 'react';
import { Card, Group, Text, Button, Box, Image, useMantineTheme } from '@mantine/core';
import { IconPlus } from '@tabler/icons-react';
import { useMediaQuery } from '@mantine/hooks';

type EmptyCookbookCTAProps = {
  onImportRecipes: () => void;
};

export const EmptyCookbookCTA: React.FC<EmptyCookbookCTAProps> = ({
  onImportRecipes
}) => {
  const theme = useMantineTheme();
  const isDesktop = useMediaQuery(`(min-width: ${theme.breakpoints.md})`);
  const isMobile = useMediaQuery(`(max-width: ${theme.breakpoints.sm})`);
  
  if (isMobile) {
    // Mobile version with vertical layout
    return (
      <Card 
        radius="lg"
        p="md"
        style={{ 
          background: 'linear-gradient(135deg, #fef7f0 0%, #fff7ed 100%)',
          border: '1px solid #fed7aa',
          boxShadow: '0 1px 3px rgba(194, 65, 12, 0.08)'
        }}
      >
        <Group align="center" mb="sm">
          {/* Small recipe photo as accent */}
          <Box 
            style={{ 
              width: 48,
              height: 48,
              borderRadius: '50%',
              overflow: 'hidden',
              flexShrink: 0,
              border: '2px solid #fed7aa'
            }}
          >
            <Image
              src="/images/recipe-example-1.png"
              alt="Recipe example"
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover'
              }}
            />
          </Box>
          
          <Box style={{ flex: 1 }}>
            <Text fw={600} size="sm" c="dark" mb={2}>
              Import your favorite recipes
            </Text>
            <Text size="xs" c="dimmed">
              Upload photos or URLs to personalize your meal plans
            </Text>
          </Box>
        </Group>
        
        <Button 
          size="sm"
          variant="light"
          leftSection={<IconPlus size={16} />}
          onClick={onImportRecipes}
          fullWidth
          style={{ 
            fontWeight: 600
          }}
        >
          Add recipes
        </Button>
      </Card>
    );
  }
  
  return (
    <Card 
      radius="xl"
      p={0}
      style={{ 
        overflow: 'hidden',
        background: 'white',
        position: 'relative',
        border: '1px solid rgba(0, 0, 0, 0.06)',
        transition: 'all 0.2s ease'
      }}
      className="premium-cta"
    >
      <Group gap={0} align="stretch">
        {/* Recipe Photos Collage */}
        <Box 
          style={{ 
            width: 200,
            height: 110,
            position: 'relative',
            overflow: 'hidden',
            flexShrink: 0
          }}
        >
          {/* Main photo */}
          <Box
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: 115,
              height: '100%'
            }}
          >
            <Image
              src="/images/recipe-example-1.png"
              alt="Recipe example"
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                filter: 'brightness(1.05) contrast(1.1) saturate(1.1)'
              }}
            />
          </Box>
          
          {/* Top right photo */}
          <Box
            style={{
              position: 'absolute',
              top: 0,
              right: 0,
              width: 85,
              height: 55
            }}
          >
            <Image
              src="/images/recipe-example-2.png"
              alt="Recipe example"
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                filter: 'brightness(1.05) contrast(1.1) saturate(1.1)'
              }}
            />
          </Box>
          
          {/* Bottom right photo */}
          <Box
            style={{
              position: 'absolute',
              bottom: 0,
              right: 0,
              width: 85,
              height: 55
            }}
          >
            <Image
              src="/images/recipe-example-3.png"
              alt="Recipe example"
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                filter: 'brightness(1.05) contrast(1.1) saturate(1.1)'
              }}
            />
          </Box>

          {/* Premium overlay gradient */}
          <Box
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'linear-gradient(90deg, transparent 50%, rgba(0,0,0,0.03) 80%, rgba(255,255,255,0.1) 100%)',
              pointerEvents: 'none'
            }}
          />
        </Box>

        {/* Content Section */}
        <Box style={{ 
          flex: 1, 
          padding: isDesktop ? 'var(--mantine-spacing-lg)' : 'var(--mantine-spacing-md)',
          background: 'linear-gradient(135deg, rgba(255,255,255,0.95) 0%, rgba(248,250,252,0.95) 100%)'
        }}>
          <Group justify="space-between" align="center" h="100%">
            <Box style={{ flex: 1 }}>
              <Text 
                fw={700} 
                size={isDesktop ? "md" : "sm"}
                c="dark" 
                mb={isDesktop ? 6 : 4}
                style={{ 
                  letterSpacing: '-0.01em',
                  lineHeight: 1.2
                }}
              >
                Import your favorite recipes
              </Text>
              <Text 
                size={isDesktop ? "sm" : "xs"}
                c="dimmed" 
                style={{ 
                  lineHeight: isDesktop ? 1.4 : 1.3,
                  color: 'rgba(0,0,0,0.65)'
                }}
              >
                {isDesktop 
                  ? "Upload photos or URLs to add personal touches to your meal plans"
                  : "Upload photos or URLs to personalize your meal plans"
                }
              </Text>
            </Box>
            
            <Button 
              size={isDesktop ? "md" : "sm"}
              variant="gradient"
              gradient={{ from: 'orange', to: 'red', deg: 45 }}
              leftSection={<IconPlus size={isDesktop ? 16 : 14} />}
              onClick={onImportRecipes}
              style={{ 
                flexShrink: 0,
                marginLeft: isDesktop ? 'var(--mantine-spacing-lg)' : 'var(--mantine-spacing-md)',
                fontWeight: 600,
                letterSpacing: '0.01em',
                boxShadow: '0 2px 8px rgba(255, 107, 0, 0.25)',
                border: 'none',
                width: isDesktop ? '180px' : 'auto'
              }}
            >
              Add recipes
            </Button>
          </Group>
        </Box>
      </Group>
    </Card>
  );
};
